import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listSitesForReview,
  getSiteReviewHistory,
  reviewSiteAction,
  getReviewStats,
  type ReviewStatus,
  type ReviewAction,
  type SiteForReview,
} from "@/lib/catalog-review.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Clock, RotateCcw, ExternalLink, MapPin, History } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/admin/overovani-adres")({
  component: AdminAddressReviewPage,
  head: () => ({
    meta: [
      { title: "Ověřování adres pivovarů — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

const STATUS_LABELS: Record<ReviewStatus | "ALL", string> = {
  ALL: "Vše",
  DISCOVERY_ONLY: "Discovery only",
  PENDING_REVIEW: "K ověření",
  VERIFIED: "Ověřeno",
  REJECTED: "Zamítnuto",
};

const STATUS_BADGE: Record<ReviewStatus, { cls: string; label: string }> = {
  DISCOVERY_ONLY: { cls: "bg-muted text-muted-foreground border-border", label: "Discovery only" },
  PENDING_REVIEW: { cls: "bg-yellow-100 text-yellow-900 border-yellow-300", label: "K ověření" },
  VERIFIED:       { cls: "bg-green-100 text-green-900 border-green-300", label: "Ověřeno" },
  REJECTED:       { cls: "bg-red-100 text-red-900 border-red-300", label: "Zamítnuto" },
};

function AdminAddressReviewPage() {
  const qc = useQueryClient();
  const { hasRole, roles } = useAuth();
  const canReview = hasRole("admin") || hasRole("catalog_editor") || hasRole("catalog_reviewer");
  const canReset = hasRole("admin") || hasRole("catalog_editor");

  const listFn = useServerFn(listSitesForReview);
  const historyFn = useServerFn(getSiteReviewHistory);
  const actionFn = useServerFn(reviewSiteAction);
  const statsFn = useServerFn(getReviewStats);

  const [status, setStatus] = useState<ReviewStatus | "ALL">("PENDING_REVIEW");
  const [search, setSearch] = useState("");
  const [historyFor, setHistoryFor] = useState<SiteForReview | null>(null);
  const [rejectFor, setRejectFor] = useState<SiteForReview | null>(null);
  const [rejectNote, setRejectNote] = useState("");

  const stats = useQuery({
    queryKey: ["cid-review-stats"],
    queryFn: () => statsFn(),
    staleTime: 30_000,
  });

  const list = useQuery({
    queryKey: ["cid-sites-review", status, search],
    queryFn: () => listFn({ data: { status, search, limit: 100 } }),
  });

  const history = useQuery({
    enabled: !!historyFor,
    queryKey: ["cid-review-history", historyFor?.id],
    queryFn: () => historyFn({ data: { siteId: historyFor!.id } }),
  });

  const mut = useMutation({
    mutationFn: async (v: { siteId: string; action: ReviewAction; note?: string }) => actionFn({ data: v }),
    onSuccess: (r) => {
      toast.success(`Stav změněn na ${STATUS_BADGE[r.to as ReviewStatus]?.label ?? r.to}`);
      qc.invalidateQueries({ queryKey: ["cid-sites-review"] });
      qc.invalidateQueries({ queryKey: ["cid-review-stats"] });
      qc.invalidateQueries({ queryKey: ["cid-review-history"] });
      qc.invalidateQueries({ queryKey: ["catalog-map-pins"] });
    },
    onError: (e: Error) => toast.error(e.message || "Akce selhala"),
  });

  if (!canReview) {
    return (
      <div className="p-6">
        <h1 className="font-serif text-2xl">Nemáte přístup</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Vyžadovaná role: admin, catalog_editor nebo catalog_reviewer. Vaše role: {roles.join(", ") || "user"}.
        </p>
      </div>
    );
  }

  const rows = list.data?.rows ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl">Ověřování adres pivovarů</h1>
        <p className="text-sm text-muted-foreground">
          Fronta lokalit z katalogu Cerveza Data Standard. Ověřená adresa dostane na mapě zelený štítek; zamítnutá se nezobrazuje na veřejném listu.
        </p>
      </div>

      {/* Stat tiles */}
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
        {(["DISCOVERY_ONLY","PENDING_REVIEW","VERIFIED","REJECTED"] as ReviewStatus[]).map((s) => (
          <Card key={s} className="cursor-pointer transition hover:border-primary" onClick={() => setStatus(s)}>
            <CardHeader className="pb-2">
              <CardDescription className="text-xs uppercase tracking-wide">{STATUS_BADGE[s].label}</CardDescription>
              <CardTitle className="text-3xl tabular-nums">
                {stats.data?.[s] ?? "—"}
              </CardTitle>
            </CardHeader>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="w-48">
          <label className="text-xs text-muted-foreground">Stav</label>
          <Select value={status} onValueChange={(v) => setStatus(v as ReviewStatus | "ALL")}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {(Object.keys(STATUS_LABELS) as (ReviewStatus | "ALL")[]).map((s) => (
                <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-64">
          <label className="text-xs text-muted-foreground">Hledat název</label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="např. Brasserie du Bahus" />
        </div>
        <div className="text-sm text-muted-foreground pb-2">
          {list.isLoading ? "Načítám…" : `${list.data?.total ?? 0} záznamů`}
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-3 text-left">Pivovar / lokalita</th>
                  <th className="p-3 text-left">Adresa</th>
                  <th className="p-3 text-left">Stav</th>
                  <th className="p-3 text-left">GPS</th>
                  <th className="p-3 text-right">Akce</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !list.isLoading && (
                  <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Žádné záznamy pro tento filtr.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id} className="border-t align-top">
                    <td className="p-3">
                      <div className="font-medium">{r.official_name}</div>
                      <div className="text-xs text-muted-foreground">{r.external_id ?? "—"}</div>
                    </td>
                    <td className="p-3">
                      <div>{r.address_line_1 || <span className="text-muted-foreground italic">bez ulice</span>}</div>
                      <div className="text-xs text-muted-foreground">
                        {[r.postal_code, r.city, r.admin_area, r.country_iso2].filter(Boolean).join(" · ") || "—"}
                      </div>
                      {r.rejection_reason && (
                        <div className="mt-1 text-xs text-red-700">Důvod zamítnutí: {r.rejection_reason}</div>
                      )}
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className={STATUS_BADGE[r.review_status].cls}>
                        {STATUS_BADGE[r.review_status].label}
                      </Badge>
                      {r.reviewed_at && (
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          {new Date(r.reviewed_at).toLocaleString("cs")}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-xs tabular-nums">
                      {r.latitude != null && r.longitude != null ? (
                        <a
                          href={`https://www.openstreetmap.org/?mlat=${r.latitude}&mlon=${r.longitude}#map=17/${r.latitude}/${r.longitude}`}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          <MapPin className="h-3 w-3" /> {r.latitude.toFixed(4)}, {r.longitude.toFixed(4)}
                        </a>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap justify-end gap-1">
                        {r.review_status !== "PENDING_REVIEW" && (
                          <Button size="sm" variant="outline" title="Odeslat k ověření"
                            onClick={() => mut.mutate({ siteId: r.id, action: "submit_for_review" })}
                            disabled={mut.isPending}>
                            <Clock className="h-3 w-3" />
                          </Button>
                        )}
                        {r.review_status !== "VERIFIED" && (
                          <Button size="sm" variant="outline" title="Ověřit" className="text-green-700 hover:bg-green-50"
                            onClick={() => mut.mutate({ siteId: r.id, action: "verify" })}
                            disabled={mut.isPending}>
                            <CheckCircle2 className="h-3 w-3" />
                          </Button>
                        )}
                        {r.review_status !== "REJECTED" && (
                          <Button size="sm" variant="outline" title="Zamítnout" className="text-red-700 hover:bg-red-50"
                            onClick={() => { setRejectFor(r); setRejectNote(""); }}
                            disabled={mut.isPending}>
                            <XCircle className="h-3 w-3" />
                          </Button>
                        )}
                        {canReset && r.review_status !== "DISCOVERY_ONLY" && (
                          <Button size="sm" variant="ghost" title="Reset na Discovery only"
                            onClick={() => mut.mutate({ siteId: r.id, action: "reset" })}
                            disabled={mut.isPending}>
                            <RotateCcw className="h-3 w-3" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" title="Historie"
                          onClick={() => setHistoryFor(r)}>
                          <History className="h-3 w-3" />
                        </Button>
                        <a href={`/provozovna/${r.id}`} target="_blank" rel="noreferrer"
                          title="Detail" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Reject dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Zamítnout adresu</DialogTitle>
            <DialogDescription>
              {rejectFor?.official_name}. Uveďte důvod (bude vidět v historii).
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder="Např. adresa neodpovídá — na místě není pivovar, GPS ukazuje na obecní úřad."
            rows={4}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectFor(null)}>Zrušit</Button>
            <Button
              variant="destructive"
              disabled={!rejectNote.trim() || mut.isPending}
              onClick={() => {
                if (!rejectFor) return;
                mut.mutate({ siteId: rejectFor.id, action: "reject", note: rejectNote.trim() });
                setRejectFor(null);
              }}
            >Zamítnout</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Historie ověření</DialogTitle>
            <DialogDescription>{historyFor?.official_name}</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {history.isLoading && <p className="text-sm text-muted-foreground">Načítám…</p>}
            {history.data?.length === 0 && <p className="text-sm text-muted-foreground">Zatím žádný audit záznam.</p>}
            <ul className="space-y-2">
              {history.data?.map((h) => (
                <li key={h.id} className="rounded-md border p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{h.action}</span>
                    <span className="text-xs text-muted-foreground">{new Date(h.created_at).toLocaleString("cs")}</span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {h.from_status ?? "—"} → <span className="font-medium text-foreground">{h.to_status}</span>
                  </div>
                  {h.note && <div className="mt-2 whitespace-pre-wrap">{h.note}</div>}
                </li>
              ))}
            </ul>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
